(ns bec-core.cljs.views.leaderboard
  (:require [re-frame.core :as rf]
            [reagent.core :as r]
            [bec-cljs-common.components.button :as button]
            [bec-cljs-common.components.player :as p]
            [bec-cljs-common.utils.formatting :as fmt]
            [bec-cljs-common.i18n :as i18n]
            [clojure.string :as s]))

(defn rgb-color [colors default-teams color-key alpha]
  (when-let [c (get-in colors [(:color (get default-teams color-key)) :color])]
    (str "rgba(" c ", " (or alpha 1) ")")))

(defn rgb-color-dark [colors default-teams color-key alpha]
  (when-let [c (get-in colors [(:color (get default-teams color-key)) :dark])]
    (str "rgba(" c ", " (or alpha 1) ")")))

(defn calculate-score [players]
  (->> players
    (map :total-score)
    (reduce +)))

(defn add-score [team-entry]
  (let [col (key team-entry)
        players (val team-entry)
        score (calculate-score players)]
    {:color col
     :total-score score
     :players players}))

(defn teams-by-score [players]
  (->> players
    (group-by :color)
    (map add-score)
    (sort-by :total-score)
    reverse))

(defn row-container [{:keys [colors default-teams player style]}]
  (into
    [:div
     {:style (merge
               {:position "relative"
                :display "flex"
                :align-items "center"
                :width "98%"
                :box-shadow "0 -1px 1px hsla(var(--scheme-invert-hsl), 0.3)"
                :border-radius "0.75em"
                :overflow "hidden"
                :margin "0.25em 0"
                :background (rgb-color-dark colors default-teams (:color player) 0.2)}
               style)}]
    (r/children (r/current-component))))

(defn row-element-text [& [props]]
  (into
    [:p {:key (:key props)
         :style (merge {:font-family "var(--secondary-game-font-family)"
                        :font-weight "800"
                        :font-size "1.25em"}
                  (:style props))}]
    (r/children (r/current-component))))

(defn row-element-box [& [props]]
  (into
    [:div
     {:key (:key props)
      :style (merge
               {:text-align "center"
                :display "flex"
                :justify-content "center"
                :align-items "center"
                :height "100%"
                :width "100%"
                :min-width "5em"
                :max-width "6em"
                :z-index "1"
                :font-family "var(--secondary-game-font-family)"
                :border-left (when (= (:side props) :left) "1px solid hsla(var(--scheme-invert-hsl), 0.25)")
                :border-right (when (= (:side props) :right) "1px solid hsla(var(--scheme-invert-hsl), 0.25)")}
               (:style props))}]
    (r/children (r/current-component))))

(defn player-row-element [{:keys [colors default-teams player side]}]
  (let [align-side (if (= side :left) "start" "end")
        player-circle-component [p/player-circle
                                 {:size "2.75em"
                                  :color (rgb-color colors default-teams (:color player) 1)
                                  :player player}]
        default-teams (or default-teams @(rf/subscribe [:default-teams-sorted]))
        enable-more-teams? (or (:enable-more-teams? player) @(rf/subscribe [:enable-more-teams?]))
        display-name (:display-name player)]
    [row-element-box
     {:style {:min-width "13.5em"
              :min-height "3.25em"
              :height "100%"
              :width "100%"
              :max-width "none"
              :display "flex"
              :justify-content align-side}
      :side side}
     (when (= side :left) player-circle-component)
     [:div
      {:style {:display "flex"
               :justify-content align-side
               :flex-direction "column"
               :overflow "hidden"}}
      [:p.text-overflow
       {:style {:text-align align-side
                :font-size "1.25em"
                :font-weight "600"}}
       (:nick player)]
      [:p.text-overflow
       {:style {:text-align align-side
                :color (rgb-color colors default-teams (:color player) 1)
                :filter "brightness(1.15)"
                :font-size "0.75em"
                :font-weight "400"
                :text-transform "uppercase"
                :margin-top "-0.25em"}}
       (str (i18n/get {:fallback-phrase "Team"} [:teams :team]) " "
         (i18n/get {:fallback-phrase "Color"}
           [:teams (:color (get default-teams (:color player))) :name])
         (if (> (:index (get default-teams (:color player))) 6)
           " 2"
           (when enable-more-teams? " 1"))
         " on " display-name)]]
     (when-not (= side :left) player-circle-component)]))

(defn background-blurred-circles [& [props]]
  (let [width (or (:size (:style props)) "35vw")
        height (or (:size (:style props)) "35vw")]
    [:div
     {:style (merge
               {:position "absolute"
                :width width
                :height height
                :transition "background var(--transition-duration) ease"
                :filter "blur(11vw)"}
               (dissoc (:style props) :size))}]))

(defn teams-leaderboard [{:keys [players colors default-teams history heading size]}]
  (let [colors (or colors @(rf/subscribe [:colors]))
        app-category @(rf/subscribe [:app-category])
        history (or history @(rf/subscribe [:history]))
        players-stats (group-by :player-id history)
        history-by-stat-kind (group-by :stat-kind history)
        stat-kind-counts (reverse
                           (sort-by :count
                             (map
                               (fn [val]
                                 {:stat-kind (first val)
                                  :count (count (second val))})
                               (dissoc history-by-stat-kind :score))))
        stat-kinds (map (fn [val] {:stat-kind (first val)
                                   :stat-path (:stat-path (first (second val)))})
                     (if (= app-category "shuffle")
                       (select-keys (dissoc history-by-stat-kind :score) [:knockout :gutter])
                       (if (> (count stat-kind-counts) 2)
                         (select-keys (dissoc history-by-stat-kind :score)
                           [(:stat-kind (first stat-kind-counts)) (:stat-kind (second stat-kind-counts))])
                         (dissoc (group-by :stat-kind history) :score))))
        players-modified (map
                           (fn [val]
                             (assoc val :stats (get players-stats (:id val))))
                           players)
        players-with-positions (map-indexed
                                 (fn [index player]
                                   (assoc player :ladder-position (inc index)))
                                 (reverse
                                   (sort-by :total-score
                                     (map
                                       (fn [player]
                                         (assoc player :total-score
                                                       (reduce
                                                         (fn [a m]
                                                           (+ a (:stat-value m)))
                                                         0 (:stats player))))
                                       players-modified))))
        teams-sorted (teams-by-score players-with-positions)
        teams-partitioned (partition-all 2 teams-sorted)
        get-rgba-color-fn (fn [color-key alpha] (rgb-color colors default-teams color-key alpha))
        get-rgba-dark-color-fn (fn [color-key alpha] (rgb-color-dark colors default-teams color-key alpha))
        first-place-color (get-rgba-color-fn (:color (first (first teams-partitioned))) 1)
        second-place-color (get-rgba-color-fn (:color (second (first teams-partitioned))) 1)
        size (or size 0.78)
        size-rem (str (+ size (if (< (count players-with-positions) 2)
                                (/ size 2)
                                (/ size (count players-with-positions)))) "rem")]
    (conj
      [:div.fade-in
       {:style {"--fade-in-duration" "1000ms"
                :font-size size-rem
                :position "relative"
                :display "flex"
                :flex-direction "column"
                :height "100%"
                :min-height "100vh"
                :overflow "hidden"
                :background "var(--scheme-main-dark)"}}
       [:div
        {:style {:height "100%"
                 :width "100%"
                 :padding "0 4vw"
                 :overflow "auto"}}
        [:div
         {:style {:min-width "66.5rem"
                  :min-height "calc(100% - 6rem)"
                  :display "flex"
                  :justify-content "center"
                  :align-items "center"
                  :flex-wrap "wrap"
                  :flex-direction "column"
                  :margin-bottom "1.5rem"}}
         [:div {:style {:width "100%"
                        :font-family "var(--primary-game-font-family)"
                        :text-transform "uppercase"
                        :font-size "max(6vmin, 2.5rem)"
                        :font-style "oblique 40deg"
                        :text-align "center"
                        :min-width "66.5rem"
                        :color "var(--text)"}}
          (i18n/get {:fallback-phrase "Leaderboard"} [:games :leaderboard])]
         (into
           [:div
            {:style {:width "100%"
                     :z-index "2"}}
            [:div
             {:style {:display "flex"
                      :justify-content "space-between"
                      :width "100%"}}
             [:div
              {:style {:width "50%"
                       :display "flex"
                       :align-items "start"
                       :flex-direction "column"}}
              (conj
                (into
                  [row-container
                   {:colors colors
                    :default-teams default-teams
                    :style {:width "100%"
                            :padding-right "2.5%"
                            :box-shadow "none"
                            :border-radius "none"
                            :background "none"}}
                   [row-element-box
                    {:style {:max-width "2.5em"
                             :min-width "2em"}}]
                   [row-element-box
                    {:style {:min-width "13.5em"
                             :max-width "none"
                             :height "100%"
                             :width "100%"}}]]
                  (map-indexed
                    (fn [index stat]
                      [row-element-box {:key (str "left-labels-" index)}
                       [row-element-text
                        {:style {:font-weight "200"
                                 :font-size "1em"}}
                        (i18n/get {:fallback-phrase "Stat"} (:stat-path stat))]])
                    stat-kinds))
                [row-element-box
                 [row-element-text
                  {:style {:font-weight "200"
                           :font-size "1em"}}
                  (i18n/get {:fallback-phrase "Score"} [:ranking :score])]])]
             [:div
              {:style {:width "50%"
                       :display "flex"
                       :align-items "start"
                       :flex-direction "column"}}
              (conj
                (conj
                  (into
                    [row-container
                     {:colors colors
                      :default-teams default-teams
                      :style {:width "100%"
                              :padding-left "2.5%"
                              :box-shadow "none"
                              :border-radius "none"
                              :background "none"}}
                     [row-element-box
                      [row-element-text
                       {:style {:font-weight "200"
                                :font-size "1em"}}
                       (i18n/get {:fallback-phrase "Score"} [:ranking :score])]]]
                    (map-indexed
                      (fn [index stat]
                        [row-element-box
                         {:key (str "right-labels-" index)}
                         [row-element-text
                          {:style {:font-weight "200"
                                   :font-size "1em"}}
                          (i18n/get {:fallback-phrase "Stat"} (:stat-path stat))]])
                      (reverse stat-kinds)))
                  [row-element-box
                   {:style {:min-width "13.5em"
                            :max-width "none"
                            :height "100%"
                            :width "100%"}}])
                [row-element-box
                 {:style {:max-width "2.5em"
                          :min-width "2em"}}])]]]
           (map-indexed
             (fn [idx-1 teams]
               [:div
                (when-not (= idx-1 0)
                  [:hr
                   {:style {:width "100%"
                            :height "1px"
                            :margin "0.25em auto"
                            :background "hsla(var(--scheme-invert-hsl), 0.25)"}}])
                [:div
                 {:style {:display "flex"
                          :justify-content "space-between"
                          :width "100%"}}
                 (into
                   [:div
                    {:style {:width "50%"
                             :display "flex"
                             :align-items "start"
                             :flex-direction "column"}}]
                   (map-indexed
                     (fn [index player]
                       (conj
                         (into
                           [row-container
                            {:colors colors
                             :default-teams default-teams
                             :player player}
                            [:div
                             {:style {:position "absolute"
                                      :width "100%"
                                      :height "100%"
                                      :background (str "radial-gradient(46% 115% at 50% 85%, "
                                                    (get-rgba-color-fn (:color (first teams)) 0.8)
                                                    " 0%, transparent 100%)")}}]
                            [row-element-box
                             {:style {:border-left "none"
                                      :max-width "2.5em"
                                      :min-width "2em"}
                              :side :left}
                             [row-element-text (:ladder-position player)]]
                            [player-row-element
                             {:player player
                              :colors colors
                              :default-teams default-teams
                              :side :left}]]
                           (map-indexed
                             (fn [index stat]
                               (let [stat-score (reduce
                                                  (fn [a m] (+ a (:stat-value m)))
                                                  0 (get (:stats player) (:stat-kind stat)))]
                                 [row-element-box
                                  {:key (str "left-stats-" index)
                                   :side :left}
                                  [row-element-text stat-score]]))
                             stat-kinds))
                         [row-element-box
                          {:style {:background (get-rgba-dark-color-fn (:color (first teams)) 0.5)}
                           :side :left}
                          [row-element-text (fmt/round-number (:total-score player))]]))
                     (:players (first teams))))
                 (into
                   [:div
                    {:style {:width "50%"
                             :display "flex"
                             :align-items "end"
                             :flex-direction "column"}}]
                   (map-indexed
                     (fn [index player]
                       (conj
                         (conj
                           (into
                             [row-container
                              {:colors colors
                               :default-teams default-teams
                               :player player}
                              [:div
                               {:style {:position "absolute"
                                        :width "100%"
                                        :height "100%"
                                        :background (str "radial-gradient(46% 115% at 50% 85%, "
                                                      (get-rgba-color-fn (:color (second teams)) 0.8)
                                                      " 0%, transparent 100%)")}}]
                              [row-element-box
                               {:style {:background (get-rgba-color-fn (:color (second teams)) 0.3)}
                                :side :right}
                               [row-element-text (fmt/round-number (:total-score player))]]]
                             (map-indexed
                               (fn [index stat]
                                 (let [stat-score (reduce
                                                    (fn [a m] (+ a (:stat-value m)))
                                                    0 (get (group-by :stat-kind (:stats player)) (:stat-kind stat)))]
                                   [row-element-box
                                    {:key (str "right-stats-" index)
                                     :side :right}
                                    [row-element-text stat-score]]))
                               stat-kinds))
                           [player-row-element
                            {:player player
                             :colors colors
                             :default-teams default-teams
                             :side :right}])
                         [row-element-box
                          {:style {:border-right "none"
                                   :max-width "2.5em"
                                   :min-width "2em"}
                           :side :right}
                          [row-element-text (:ladder-position player)]]))
                     (:players (second teams))))]])
             teams-partitioned))]]]
      [background-blurred-circles
       {:style {:left "-15vw"
                :top "-3vw"
                :background first-place-color
                :opacity "0.7"
                :transform "rotate(38deg)"}}]
      [background-blurred-circles
       {:style {:right "-15vw"
                :bottom "-3vw"
                :background second-place-color
                :opacity "0.7"
                :transform "rotate(38deg)"}}])))

(defn view [& [props]]
  (let [observed-state @(rf/subscribe [:observed-state])
        players (reduce-kv
                  (fn [result k v]
                    (merge result
                      (reduce-kv
                        (fn [players-map k player]
                          (assoc players-map k (assoc player
                                                 :display-name (:display-name (:config v))
                                                 :enable-more-teams? (:enable-more-teams? (:game (:bec-clj-common v))))))
                        {}
                        (:players (:bec-clj-common v)))))
                  {}
                  observed-state)
        colors (reduce-kv
                 (fn [result k v]
                   (merge result (:colors (:clients (:config v)))))
                 {}
                 observed-state)
        default-teams (reduce-kv
                        (fn [result k v]
                          (merge result (:default-teams (:clients (:config v)))))
                        {}
                        observed-state)
        history (reduce-kv
                  (fn [result k v]
                    (into result (:history v)))
                  []
                  observed-state)]
    (when-not (empty? players)
      [teams-leaderboard
       {:players (filter #(not (:benched? %))
                   (->> players
                     vals
                     (sort-by :update-ts)))
        :history history
        :colors colors
        :default-teams default-teams}])))

